export interface SpeciesSource {
  key: string;
  bookId: string;
  bookTitle: string;
  chapter?: string;
  page?: number;
  entryNames: string[];
  excerpts: string[];
  chunkIds: string[];
}

type RecordValue = Record<string, unknown>;
function record(value: unknown): RecordValue {
  return value && typeof value === 'object' ? value as RecordValue : {};
}
function string(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
function pageNumber(value: unknown): number | undefined {
  const page = typeof value === 'number' ? value : /^\d+$/.test(string(value)) ? Number(value) : NaN;
  return Number.isInteger(page) && page > 0 && page <= 2147483647 ? page : undefined;
}
function field(text: string, label: string): string {
  return text.match(new RegExp(`(?:^|\\n)\\s*${label}[：:]\\s*([^\\n]+)`))?.[1]?.trim() || '';
}
function excerpt(text: string): string {
  if (!/^\s*书名[：:]/.test(text)) return text.trim();
  // Catalog metadata is not a readable description. Keep meaningful prose only.
  return field(text, '地位作用') || field(text, '简介') || field(text, '开发利用情况');
}

/** Merge rich citations with direct graph references, without treating graph chunks as cards. */
export function collectSpeciesSources(graphValue: unknown, centerId: string, richSources: unknown[] = [], books: unknown[] = []): SpeciesSource[] {
  const graph = record(graphValue);
  const nodes = Array.isArray(graph.nodes) ? graph.nodes.map(record) : [];
  const edges = Array.isArray(graph.edges) ? graph.edges.map(record) : [];
  const byId = new Map(nodes.map((node) => [string(node.id), node]));
  const bookNames = new Map(books.map((item) => { const book = record(item); return [string(book.id), string(book.title)]; }));
  const candidates: Array<Omit<SpeciesSource, 'key'>> = [];
  const knownChunks = new Set<string>();

  function add(raw: RecordValue, chunkId: string) {
    const bookId = string(raw.book_id);
    if (!bookId) return; // Do not invent a navigation target for an unbound fragment.
    const body = string(raw.text) || string(raw.snippet);
    const name = field(body, '资源名称');
    const prose = excerpt(body);
    candidates.push({
      bookId,
      bookTitle: string(raw.book_title) || bookNames.get(bookId) || field(body, '书名') || '相关图书',
      chapter: string(raw.chapter_title) || field(body, '章节') || undefined,
      page: pageNumber(raw.page),
      entryNames: name ? [name] : [],
      excerpts: prose ? [prose] : [],
      chunkIds: chunkId ? [chunkId] : [],
    });
    if (chunkId) knownChunks.add(chunkId);
  }

  richSources.forEach((source) => { const raw = record(source); add(raw, string(raw.chunk_id)); });
  for (const edge of edges) {
    if (edge.label !== 'MENTIONED_IN') continue;
    const neighborId = edge.from_id === centerId ? string(edge.to_id) : edge.to_id === centerId ? string(edge.from_id) : '';
    const node = byId.get(neighborId);
    if (!node || knownChunks.has(neighborId)) continue;
    if (node.label === 'Chunk') add(record(node.properties), neighborId);
    if (node.label === 'BookRef') {
      const props = record(node.properties);
      add({ ...props, book_id: props.book_id || node.id, book_title: props.title || node.name }, '');
    }
  }

  // A graph record without chapter metadata can join one unambiguous rich citation
  // for the same book/page; never collapse distinct chapters or different pages.
  for (const source of candidates) {
    if (source.chapter || source.page == null) continue;
    const chapters = new Set(candidates.filter((other) => other.bookId === source.bookId && other.page === source.page && other.chapter).map((other) => other.chapter));
    if (chapters.size === 1) source.chapter = [...chapters][0];
  }
  const grouped = new Map<string, SpeciesSource>();
  for (const source of candidates) {
    // Missing page metadata must not merge unrelated unknown-page fragments.
    const location = source.page ?? `unknown:${source.chunkIds[0] || source.bookId}`;
    const key = JSON.stringify([source.bookId, source.chapter || '', location]);
    const existing = grouped.get(key);
    if (!existing) grouped.set(key, { ...source, key });
    else {
      existing.entryNames = [...new Set([...existing.entryNames, ...source.entryNames])];
      existing.excerpts = [...new Set([...existing.excerpts, ...source.excerpts])];
      existing.chunkIds = [...new Set([...existing.chunkIds, ...source.chunkIds])];
      if (existing.bookTitle === '相关图书') existing.bookTitle = source.bookTitle;
    }
  }
  return [...grouped.values()];
}

export function sourcePdfHref(source: SpeciesSource, previewableBookIds: string[]): string | undefined {
  return pageNumber(source.page) && previewableBookIds.includes(source.bookId)
    ? `/source-page/${encodeURIComponent(source.bookId)}/${source.page}` : undefined;
}
